
import React, { useEffect, useRef } from "react";

import { IconButton, Tooltip, FormControl, MenuItem, Select } from "@material-ui/core";

import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListNumbered,
  FormatListBulleted,
  Link,
  FormatClear,
  Undo,
  Redo,
} from "@material-ui/icons";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastInternalValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;
  
    if (lastInternalValueRef.current === value) {
      lastInternalValueRef.current = null;
      return;
    }
  
    if (editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const handleEditorChange = () => {
    if (!editorRef.current) return;
  
    const html = editorRef.current.innerHTML;
    lastInternalValueRef.current = html;
  
    onChange(html);
  };

  const executeCommand = (
    command: string,
    commandValue?: string
  ) => {
    editorRef.current?.focus();

    document.execCommand(
      command,
      false,
      commandValue
    );

    handleEditorChange();
  };

  const handleHeadingChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const heading = event.target.value as string;
  
    executeCommand("formatBlock", heading);
  };
  

  const handleLink = () => {
    const url = window.prompt("Enter the URL:");

    if (url && url.trim()) {
      executeCommand("createLink", url.trim());
    }
  };

  const preventToolbarFocusLoss = (
    event: React.MouseEvent
  ) => {
    event.preventDefault();
  };

  return (
    <div className="rich-text-editor">
      <div className="rich-text-label">
        Message *
      </div>

      <div className="rich-text-toolbar">
      <FormControl
            variant="outlined"
            size="small"
            className="heading-select"
        >
           <Select
                defaultValue="div"
                onChange={handleHeadingChange}
            >
                <MenuItem value="div">Normal</MenuItem>
                <MenuItem value="h1">Heading 1</MenuItem>
                <MenuItem value="h2">Heading 2</MenuItem>
                <MenuItem value="h3">Heading 3</MenuItem>
                <MenuItem value="p">Paragraph</MenuItem>
            </Select>
        </FormControl>
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() => executeCommand("bold")}
          >
            <FormatBold />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() => executeCommand("italic")}
          >
            <FormatItalic />
          </IconButton>
        </Tooltip>

        <Tooltip title="Underline">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() => executeCommand("underline")}
          >
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() => executeCommand("strikeThrough")}
          >
            <FormatStrikethrough />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bulleted List">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() =>
              executeCommand("insertUnorderedList")
            }
          >
            <FormatListBulleted />
          </IconButton>
        </Tooltip>

        <Tooltip title="Numbered List">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={() =>
              executeCommand("insertOrderedList")
            }
          >
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        <Tooltip title="Insert Link">
          <IconButton
            size="small"
            onMouseDown={preventToolbarFocusLoss}
            onClick={handleLink}
          >
            <Link />
          </IconButton>
        </Tooltip>
        <Tooltip title="Undo">
            <IconButton
                size="small"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => executeCommand("undo")}
            >
                <Undo />
            </IconButton>
        </Tooltip>
         <Tooltip title="Redo">
            <IconButton
                size="small"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => executeCommand("redo")}
            >
                <Redo />
            </IconButton>
        </Tooltip>
        <Tooltip title="Clear Formatting">
            <IconButton
                size="small"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => executeCommand("removeFormat")}
            >
                <FormatClear />
            </IconButton>
        </Tooltip>
      </div>

      <div
        ref={editorRef}
        className="rich-text-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleEditorChange}
      />
    </div>
  );
};